import { useBlocker } from "@tanstack/react-router";
import { useCallback, useState } from "react";

/** What a draft-and-save page hands the guard. */
export interface UnsavedChangesGuardOptions {
  /** True while the page holds changes it has not saved. The guard does nothing while false. */
  dirty: boolean;
  /**
   * Saves the draft; resolves once the save succeeded and rejects when it failed. Given, the
   * dialog offers Save and leave. A failure keeps the page where it is, and the query client's
   * mutation toast says why, so this needs no error handling of its own (`mutateAsync` fits).
   */
  onSave?: () => Promise<unknown>;
  /** Drops the draft before the navigation goes ahead on Discard and leave. */
  onDiscard?: () => void;
}

/** What `UnsavedChangesDialog` reads: whether a navigation is waiting, and the three answers. */
export interface UnsavedChangesGuard {
  /** A navigation is held until the user answers the dialog. */
  blocked: boolean;
  /** Save and leave is waiting on the save. */
  saving: boolean;
  /** The page passed `onSave`, so the dialog offers Save and leave. */
  canSave: boolean;
  /** Stays on the page and drops the held navigation. */
  keepEditing: () => void;
  /** Runs `onDiscard`, then lets the held navigation go ahead. */
  discardAndLeave: () => void;
  /** Runs `onSave` and lets the held navigation go ahead once it succeeds; stays when it fails. */
  saveAndLeave: () => Promise<void>;
}

/**
 * Holds every in-app navigation (links, tabs, back and forward) while `dirty` is true until the
 * user answers `UnsavedChangesDialog`, and has the browser ask before a reload, a closed tab or
 * a link off the dashboard. While `dirty` is false no blocker is registered at all.
 *
 * A navigation the page starts itself after a successful save (the save's own redirect) passes
 * TanStack Router's `ignoreBlocker: true`: the draft's reset has not rendered yet when the save's
 * `onSuccess` runs, so the guard would still read the page as dirty.
 */
export function useUnsavedChangesGuard({
  dirty,
  onSave,
  onDiscard,
}: UnsavedChangesGuardOptions): UnsavedChangesGuard {
  const [saving, setSaving] = useState(false);
  // Only registered while dirty (`disabled` below), so every navigation it sees is one to hold.
  const shouldBlockFn = useCallback(() => true, []);
  const blocker = useBlocker({
    shouldBlockFn,
    enableBeforeUnload: true,
    disabled: !dirty,
    withResolver: true,
  });

  const keepEditing = () => {
    if (blocker.status === "blocked") blocker.reset();
  };

  const discardAndLeave = () => {
    if (blocker.status !== "blocked") return;
    onDiscard?.();
    blocker.proceed();
  };

  const saveAndLeave = async () => {
    if (blocker.status !== "blocked" || !onSave) return;
    // Captured before the save: its success resets the draft, which unregisters the blocker,
    // and the held navigation still waits on this resolver.
    const { proceed } = blocker;
    setSaving(true);
    try {
      await onSave();
    } catch {
      // The mutation's toast reports the failure; the dialog stays open on the page.
      return;
    } finally {
      setSaving(false);
    }
    proceed();
  };

  return {
    blocked: blocker.status === "blocked",
    saving,
    canSave: onSave !== undefined,
    keepEditing,
    discardAndLeave,
    saveAndLeave,
  };
}
